import { Flex, Link, Text } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { ReactComponent as OutlineInfoIcon } from 'assets/icons/outline_info.svg';
import { ReactComponent as TagIcon } from 'assets/icons/tag.svg';
import { HEADINGS_HELP_LINK } from 'constants/links';
import { HELP_OPENED } from 'constants/mixpanel';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { Toggletip } from 'modules/common/components/Toggletip';
import { DropdownPopover } from 'modules/common/components/ToolbarPopover';
import { generateTranslatedOptions } from 'modules/common/components/ToolbarPopover/DropdownPopover';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useWidget } from 'widgets/sdk';

import { TextWidgetTag } from '../../common/TextBasedWidgets.types';
import { TEXT_WIDGET_TAG_OPTIONS } from '../TextWidget.config';
import { TextWidgetData } from '../TextWidget.types';

interface TextTagProps {
  value: TextWidgetTag;
  onChange: (tag: TextWidgetTag) => void;
  onClose?: () => void;
  onOpen?: () => void;
}

const StyledOutlineInfoIcon = styled(OutlineInfoIcon)`
  & path {
    stroke: var(--vg-colors-upgrade-blue-500) !important;
  }
`;

const handleClickHelpLink = () => {
  Mixpanel.track(HELP_OPENED, {
    from: 'Text Tag Menu',
    help_type: 'Text Tag',
  });
};

const TooltipContent = memo(() => {
  const { t } = useTranslation('editor_widget_toolbar', { useSuspense: false, keyPrefix: 'textToolbar.textTag' });

  const content = t('tooltip.content');
  const linkText = t('tooltip.link');

  return (
    <>
      <Text fontSize={'xs'} mb={2} textAlign={'left'}>
        {content}
      </Text>
      <Link
        fontSize={'xs'}
        w={'fit-content'}
        variant={'inline-dark'}
        href={HEADINGS_HELP_LINK}
        isExternal
        target={'_blank'}
        onClick={handleClickHelpLink}
      >
        {linkText}
      </Link>
    </>
  );
});
TooltipContent.displayName = 'TooltipContent';

const DropdownHeader = memo(() => {
  const { t } = useTranslation('editor_widget_toolbar', { useSuspense: false, keyPrefix: 'textToolbar.textTag' });
  const title = t('headerTitle');
  const buttonAriaLabel = t('tooltip.ariaLabel');

  return (
    <Flex alignItems={'center'}>
      <Text fontSize={'sm'} color={'font.500'} fontWeight={'semibold'} mr={2}>
        {title}
      </Text>
      <Toggletip
        label={''}
        hasArrow={true}
        placement={'right'}
        icon={<StyledOutlineInfoIcon />}
        buttonAriaLabel={buttonAriaLabel}
      >
        <TooltipContent />
      </Toggletip>
    </Flex>
  );
});
DropdownHeader.displayName = 'DropdownHeader';

const BUTTON_OPTIONS = {
  fontSize: 'sm',
  fontWeight: 'normal',
  color: 'font.500',
  h: 7,
  gap: 1,
  px: 2,
  mt: '4px !important', // Using `!important` to override forced inline styling
};

export const TextTag = memo(({ value, onChange, onClose, onOpen }: TextTagProps) => {
  const selectedIndex = useMemo(() => TEXT_WIDGET_TAG_OPTIONS.findIndex((opt) => opt.value === value), [value]);
  const { effect: { type } = { type: '' } } = useWidget<TextWidgetData>();

  // If there is an effect applied, disable the dropdown since text will always be tagged as a figure
  const hasEffect = !!type;

  const { t } = useTranslation('editor_widget_toolbar', { useSuspense: false, keyPrefix: 'textToolbar.textTag' });
  const label = t('label');
  const disabledLabel = t('disabledLabel');

  return (
    <DropdownPopover
      selectedIndex={selectedIndex}
      options={generateTranslatedOptions(t, TEXT_WIDGET_TAG_OPTIONS)}
      toolbarIcon={<TagIcon />}
      label={hasEffect ? disabledLabel : label}
      onSelect={onChange}
      header={<DropdownHeader />}
      w={144}
      showSelectedIcon={true}
      onOpen={onOpen}
      onClose={onClose}
      buttonOptionProps={BUTTON_OPTIONS}
      contentOverflowY={'visible'}
      useLabelInsteadOfSelectedLabel
      headerBorderBottomWidth={0}
      triggerProps={{ isDisabled: hasEffect }}
    />
  );
});
TextTag.displayName = 'TextTag';
